import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Container,
    CircularProgress,
    Alert,
    useTheme,
    useMediaQuery,
    Paper,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Checkbox,
    FormHelperText,
    TextField,
    MenuItem,
    Select,
    TextareaAutosize,
    LinearProgress,
    ThemeProvider,
    createTheme,
    Snackbar,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon, Save as SaveIcon } from '@mui/icons-material';
import api from '../../../services/api';

const theme = createTheme({
    palette: {
        primary: {
            main: '#10641c',
        },
        secondary: {
            main: '#666666',
        },
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
    },
});

const FormsToRespond = () => {
    const { id, entrepreneur } = useParams(); 
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const typeUser = location.pathname.split('/')[1];
    const [formTemplate, setFormTemplate] = useState(null);
    const [responses, setResponses] = useState({});
    const [responseId, setResponseId] = useState(null); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [allMandatoryFilled, setAllMandatoryFilled] = useState(false);
    const [currentSectionMandatoryFilled, setCurrentSectionMandatoryFilled] = useState(false);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [isEdit, setIsEdit] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    useEffect(() => {
        const fetchFormTemplate = async () => {
            const edit = location.pathname.split('/').some(segment => segment === 'edit');
            setIsEdit(edit);
            try {
                setLoading(true);
                let response;
                if (edit) {
                    response = await api.get(`/form-templates/form/${parseInt(id)}/user/${parseInt(entrepreneur)}`);
                } else {
                    response = await api.get(`/form-templates/${parseInt(id)}`);
                }
                setFormTemplate(response.data);

                const initialResponses = {};
                let fetchedResponseId = null;
                response.data.sections.forEach((section) => {
                    const sortedFields = [...section.fields].sort((a, b) => a.order - b.order);
                    sortedFields.forEach((field) => {
                        if (edit && field.response && field.response[0] && field.response[0].FormFieldResponse) {
                            initialResponses[field.id] = {
                                response: field.response[0].FormFieldResponse.value.response || '',
                            };
                            if (!fetchedResponseId && field.response[0].id) {
                                fetchedResponseId = field.response[0].id;
                            }
                        } else {
                            if (field.type === 'checkbox') {
                                initialResponses[field.id] = { response: [] };
                            } else if (field.type === 'radio') {
                                initialResponses[field.id] = { response: null };
                            } else {
                                initialResponses[field.id] = { response: '' };
                            }
                        }
                    });
                });
                setResponses(initialResponses);
                setResponseId(fetchedResponseId);
                setError(null);
            } catch (err) {
                setError('Erro ao carregar o formulário');
                console.error('Error fetching form template:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchFormTemplate();
    }, [id, entrepreneur]);

    useEffect(() => {
        if (!formTemplate) return;

        const checkMandatoryFields = () => {
            const missingFields = formTemplate.sections
                .flatMap((section) => section.fields)
                .filter((field) =>
                    field.mandatory &&
                    (responses[field.id].response === '' ||
                        responses[field.id].response === null ||
                        (Array.isArray(responses[field.id].response) && responses[field.id].response.length === 0))
                );
            setAllMandatoryFilled(missingFields.length === 0);
        };

        const checkCurrentSectionMandatoryFields = () => {
            const currentSection = formTemplate.sections[currentSectionIndex];
            const missingFields = currentSection.fields.filter((field) =>
                field.mandatory &&
                (responses[field.id].response === '' ||
                    responses[field.id].response === null ||
                    (Array.isArray(responses[field.id].response) && responses[field.id].response.length === 0))
            );
            setCurrentSectionMandatoryFilled(missingFields.length === 0);
        };

        checkMandatoryFields();
        checkCurrentSectionMandatoryFields();
    }, [responses, formTemplate, currentSectionIndex]);

    const handleChange = (fieldId) => (event) => {
        const { value, type, checked } = event.target;
        setResponses((prevResponses) => {
            if (type === 'checkbox') {
                const currentValues = Array.isArray(prevResponses[fieldId].response) ? [...prevResponses[fieldId].response] : [];
                if (checked) {
                    currentValues.push(value);
                } else {
                    const index = currentValues.indexOf(value);
                    if (index > -1) {
                        currentValues.splice(index, 1);
                    }
                }
                return {
                    ...prevResponses,
                    [fieldId]: { response: currentValues },
                };
            } else {
                return {
                    ...prevResponses,
                    [fieldId]: { response: value },
                };
            }
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!allMandatoryFilled) {
            setError('Por favor, preencha todos os campos obrigatórios.');
            return;
        }

        try {
            setLoading(true);

            const formFieldIds = Object.keys(responses).map((id) => parseInt(id));
            const values = Object.values(responses).map((obj) => ({ response: obj.response }));

            if (isEdit) {
                if (!responseId) {
                    throw new Error('Response ID not found for edit mode');
                }
                await api.put(`/form-responses/${responseId}`, {
                    formTemplateId: formTemplate.id,
                    answeredBy: parseInt(entrepreneur),
                    formFieldIds,
                    values,
                });
                setSnackbar({
                    open: true,
                    message: 'Formulário atualizado com sucesso.',
                    severity: 'success',
                });
            } else {
                await api.post('/form-responses', {
                    formTemplateId: formTemplate.id,
                    answeredBy: parseInt(entrepreneur),
                    formFieldIds,
                    values,
                });
                setSnackbar({
                    open: true,
                    message: 'Formulário respondido com sucesso.',
                    severity: 'success',
                });
            }

            setError(null);
            setTimeout(() => {
                navigate(`/${typeUser}/DetailsEntrepreneur/${parseInt(entrepreneur)}`, {
                    state: { successMessage: isEdit ? 'Formulário atualizado com sucesso.' : 'Formulário respondido com sucesso.' },
                });
            }, 3000);
        } catch (err) {
            setError('Erro ao enviar a resposta do formulário');
            console.error('Error submitting form response:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const calculateProgress = () => {
        if (!formTemplate) return 0;
        return ((currentSectionIndex + 1) / formTemplate.sections.length) * 100;
    };

    const handleNextSection = () => {
        if (formTemplate && currentSectionIndex < formTemplate.sections.length - 1) {
            setCurrentSectionIndex(currentSectionIndex + 1);
        }
    };

    const handlePreviousSection = () => {
        if (currentSectionIndex > 0) {
            setCurrentSectionIndex(currentSectionIndex - 1);
        }
    };

    const handleBack = () => {
        if (!typeUser || !entrepreneur) {
            console.warn('Missing typeUser or entrepreneur, navigating to fallback');
            navigate(`/${typeUser}/entrepreneur`);
            return;
        }
        navigate(`/${typeUser}/DetailsEntrepreneur/${parseInt(entrepreneur)}`);
    };

    if (loading) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container sx={{ mt: 4 }}>
                <Alert severity="error">{error}</Alert>
                <Button
                    onClick={handleBack}
                    startIcon={<ArrowBackIcon />}
                    sx={{ mt: 2 }}
                >
                    Voltar
                </Button>
            </Container>
        );
    }

    if (!formTemplate) {
        return (
            <Container sx={{ mt: 4 }}>
                <Alert severity="warning">Formulário não encontrado.</Alert>
                <Button
                    onClick={handleBack}
                    startIcon={<ArrowBackIcon />}
                    sx={{ mt: 2 }}
                >
                    Voltar
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth={isMobile ? 'xs' : 'md'} sx={{ mt: 1, mb: 4 }}>
            <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom sx={{ color: '#10641c', fontWeight: 'bold' }}>
                Formulário
            </Typography>
            <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Progresso: {formTemplate.sections.length > 0 ? `${currentSectionIndex + 1}/${formTemplate.sections.length}` : '0/0'}
                </Typography>
                <LinearProgress
                    variant="determinate"
                    value={calculateProgress()}
                    sx={{
                        height: 8,
                        borderRadius: 5,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: '#10641c',
                        },
                    }}
                />
            </Box>

            <Paper
                sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 2,
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                    borderLeft: '4px solid #10641c',
                    bgcolor: '#fff',
                }}
            >
                <Typography variant={isMobile ? 'h6' : 'h5'} gutterBottom sx={{ color: '#10641c', fontWeight: 'bold' }}>
                    {formTemplate.name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {formTemplate.description}
                </Typography>
            </Paper>

            <form onSubmit={handleSubmit}>
                {formTemplate.sections.map((section, index) =>
                    index === currentSectionIndex ? (
                        <Paper
                            key={section.id}
                            sx={{
                                p: 3,
                                mb: 3,
                                borderRadius: 2,
                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                                borderLeft: '4px solid #10641c',
                                bgcolor: '#fff',
                            }}
                        >
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="h6" sx={{ color: '#10641c', fontWeight: 'bold' }}>
                                    Seção {index + 1}: {section.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {section.description}
                                </Typography>
                            </Box>
                            {[...section.fields]
                                .sort((a, b) => a.order - b.order)
                                .map((field) => (
                                    <Box key={field.id} sx={{ mb: 3, p: 2, bgcolor: '#fafafa', borderRadius: 2, borderLeft: '4px solid #10641c' }}>
                                        <FormControl fullWidth>
                                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                                                {field.label}{' '}
                                                <span style={{ color: Boolean(field.mandatory) ? 'red' : '#008000' }}>
                                                    {Boolean(field.mandatory) ? '(Obrigatório)' : '(Opcional)'}
                                                </span>
                                            </Typography>
                                            {field.description && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    {field.description}
                                                </Typography>
                                            )}
                                            {field.type === 'text' && (
                                                <TextField
                                                    fullWidth
                                                    id={field.id.toString()}
                                                    value={responses[field.id].response || ''}
                                                    onChange={handleChange(field.id)}
                                                    required={Boolean(field.mandatory)}
                                                    variant="outlined"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: '#ccc',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#10641c',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#10641c',
                                                            },
                                                        },
                                                    }}
                                                />
                                            )}
                                            {field.type === 'textarea' && (
                                                <TextareaAutosize
                                                    id={field.id.toString()}
                                                    value={responses[field.id].response || ''}
                                                    onChange={handleChange(field.id)}
                                                    required={Boolean(field.mandatory)}
                                                    minRows={4}
                                                    style={{
                                                        width: '100%',
                                                        padding: '8px 12px',
                                                        fontSize: '1rem',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ccc',
                                                        fontFamily: 'inherit',
                                                        resize: 'none',
                                                        outline: 'none',
                                                    }}
                                                    onFocus={(e) => (e.target.style.borderColor = '#10641c')}
                                                    onBlur={(e) => (e.target.style.borderColor = '#ccc')}
                                                />
                                            )}
                                            {field.type === 'radio' && field.options && (
                                                <RadioGroup
                                                    name={field.id.toString()}
                                                    value={responses[field.id].response ?? ''}
                                                    onChange={handleChange(field.id)}
                                                >
                                                    {field.options.map((option) => (
                                                        <FormControlLabel
                                                            key={option}
                                                            value={option}
                                                            control={
                                                                <Radio
                                                                    sx={{
                                                                        color: '#10641c',
                                                                        '&.Mui-checked': {
                                                                            color: '#10641c',
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label={option}
                                                        />
                                                    ))}
                                                </RadioGroup>
                                            )}
                                            {field.type === 'checkbox' && field.options && (
                                                <FormControl component="fieldset">
                                                    {field.options.map((option) => (
                                                        <FormControlLabel
                                                            key={option}
                                                            control={
                                                                <Checkbox
                                                                    checked={
                                                                        Array.isArray(responses[field.id].response) &&
                                                                        responses[field.id].response.includes(option)
                                                                    }
                                                                    onChange={handleChange(field.id)}
                                                                    value={option}
                                                                    sx={{
                                                                        color: '#10641c',
                                                                        '&.Mui-checked': {
                                                                            color: '#10641c',
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            label={option}
                                                        />
                                                    ))}
                                                </FormControl>
                                            )}
                                            {field.type === 'select' && field.options && (
                                                <Select
                                                    fullWidth
                                                    id={field.id.toString()}
                                                    value={responses[field.id].response || ''}
                                                    onChange={handleChange(field.id)}
                                                    required={Boolean(field.mandatory)}
                                                    variant="outlined"
                                                    displayEmpty
                                                    MenuProps={{
                                                        PaperProps: {
                                                            style: {
                                                                maxHeight: 150,
                                                                overflowY: 'auto',
                                                                width: 'auto',
                                                            },
                                                        },
                                                        anchorOrigin: {
                                                            vertical: 'bottom',
                                                            horizontal: 'left',
                                                        },
                                                        transformOrigin: {
                                                            vertical: 'top',
                                                            horizontal: 'left',
                                                        },
                                                        getContentAnchorEl: null,
                                                        disableScrollLock: true,
                                                        style: {
                                                            marginTop: isMobile ? '2px' : '0px',
                                                            maxHeight: 'calc(100vh - 200px)',
                                                        },
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: '#ccc',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#10641c',
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: '#008000',
                                                                borderWidth: '2px',
                                                            },
                                                        },
                                                        '& .MuiSelect-select': {
                                                            padding: '10px 14px',
                                                            '&:focus': {
                                                                borderColor: '#008000',
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <MenuItem value="" disabled>
                                                        Selecione uma opção
                                                    </MenuItem>
                                                    {field.options.map((option) => (
                                                        <MenuItem key={option} value={option}>
                                                            {option}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        </FormControl>
                                    </Box>
                                ))}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                                <Box>
                                    {currentSectionIndex > 0 && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<ArrowBackIcon />}
                                            onClick={handlePreviousSection}
                                            sx={{
                                                fontSize: isMobile ? '0.8rem' : '0.875rem',
                                                borderColor: '#666666',
                                                color: '#666666',
                                                padding: isMobile ? '6px 12px' : '8px 16px',
                                                borderRadius: '8px',
                                                transition: 'all 0.3s ease-in-out',
                                                '&:hover': {
                                                    borderColor: '#555555',
                                                    color: '#555555',
                                                    backgroundColor: '#f5f5f5',
                                                },
                                                '&:disabled': {
                                                    borderColor: '#cccccc',
                                                    color: '#cccccc',
                                                },
                                            }}
                                        >
                                            Anterior
                                        </Button>
                                    )}
                                </Box>
                                <Box>
                                    {currentSectionIndex < formTemplate.sections.length - 1 && (
                                        <Button
                                            variant="contained"
                                            endIcon={<ArrowForwardIcon />}
                                            onClick={handleNextSection}
                                            disabled={!currentSectionMandatoryFilled}
                                            sx={{
                                                fontSize: isMobile ? '0.8rem' : '0.875rem',
                                                backgroundColor: '#10641c',
                                                color: '#ffffff',
                                                padding: isMobile ? '6px 12px' : '8px 16px',
                                                borderRadius: '8px',
                                                transition: 'all 0.3s ease-in-out',
                                                '&:hover': {
                                                    backgroundColor: '#0a3d0f',
                                                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                                                    transform: 'scale(1.05)',
                                                },
                                                '&:disabled': {
                                                    backgroundColor: '#cccccc',
                                                    color: '#666666',
                                                },
                                            }}
                                        >
                                            Próximo
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    ) : null
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        sx={{
                            fontSize: isMobile ? '0.8rem' : '0.875rem',
                            borderColor: '#10641c',
                            color: '#10641c',
                            padding: isMobile ? '6px 12px' : '8px 16px',
                            borderRadius: '8px',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                borderColor: '#0a3d0f',
                                color: '#0a3d0f',
                                backgroundColor: '#e8f5e9',
                                transform: 'scale(1.05)',
                            },
                        }}
                    >
                        Voltar
                    </Button>
                    {currentSectionIndex === formTemplate.sections.length - 1 && (
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SaveIcon />}
                            disabled={loading || !allMandatoryFilled}
                            sx={{
                                fontSize: isMobile ? '0.8rem' : '0.875rem',
                                backgroundColor: '#10641c',
                                color: '#FFF',
                                padding: isMobile ? '6px 12px' : '8px 16px',
                                borderRadius: '8px',
                                transition: 'all 0.3s ease-in-out',
                                '&:hover': {
                                    backgroundColor: '#0a3d0f',
                                    color: '#FFF',
                                    border: '1px solid #0a3d0f',
                                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
                                },
                                '&:disabled': {
                                    backgroundColor: '#cccccc',
                                    color: '#666666',
                                },
                            }}
                        >
                            Salvar Resposta
                        </Button>
                    )}
                </Box>
            </form>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%', fontSize: isMobile ? '0.9rem' : '0.875rem' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

const WrappedFormsToRespond = () => (
    <ThemeProvider theme={theme}>
        <FormsToRespond />
    </ThemeProvider>
);

export default WrappedFormsToRespond;